import { useEffect, useState } from "react";
import { TextField, Dialog, DialogActions, DialogContent, DialogTitle, Button, Alert, Divider, Chip, MenuItem, Autocomplete, Typography, FormControlLabel, Checkbox } from "@mui/material";
import { useAuth } from "../../../hooks/useAuth";
import { GetTokenIdentity } from "../../../utils/Token";

import _ from "lodash";
import axios from "axios";
import Swal from "sweetalert2";

export default function CreateProductForm({ open, handleClose, fetchData, userCountry, initialData = null }) {
    const { token } = useAuth();
    const identity = GetTokenIdentity(token);
    const isMillionMappingEntry = Boolean(initialData?.million_product_code);

    const [newData, setNewData] = useState({});
    const [millionProducts, setMillionProducts] = useState([]);
    const [productGroups, setProductGroups] = useState([]);
    const [selectedMillionProduct, setSelectedMillionProduct] = useState(null);
    const [millionSearch, setMillionSearch] = useState("");

    const [errors, setErrors] = useState({ product_name: false, product_description: false, country_id: false });
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    const setError = async (field, message) => {
        await setErrors(prevErrors => ({ ...prevErrors, [field]: true }));
        await setHelperText(prevHelperText => ({ ...prevHelperText, [field]: message }));
    };

    const validate = async (field, value) => {
        if (field === "product_name" && value === "") {
            await setError("product_name", "Name is required.");
        } else if (field === "product_description" && value === "") {
            await setError("product_description", "Description is required.");
        } else if (field === "country_id" && value === "") {
            await setError("country_id", "Manage By is required.");
        } else {
            await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
            await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
        }
    };

    useEffect(() => {
        if (open) {
            const defaultCountryId = _.findIndex(userCountry, { country_id: identity.user.country_id }) < 0 ? "" : identity.user.country_id;

            setNewData({
                product_name: initialData?.product_name || "",
                product_description: initialData?.product_description || "",
                country_id: initialData?.country_id || defaultCountryId,
                product_group_id: initialData?.product_group_id || null,
                is_contract_product: Number(initialData?.is_contract_product) === 1 ? 1 : 0,
            });

            setSelectedMillionProduct(
                initialData?.million_product_code
                    ? {
                          code: initialData.million_product_code,
                          description: initialData.product_description || "",
                          price: initialData.price,
                      }
                    : null
            );

            setMillionSearch(initialData?.million_product_code || "");
            setErrors({ product_name: false, product_description: false, country_id: false });
            setHelperText({});
            setErrorMessage("");
        }
    }, [open, identity.user.country_id, userCountry, initialData]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const fetchProductGroups = async () => {
            if (!newData.country_id) {
                setProductGroups([]);
                setNewData((prev) => ({ ...prev, product_group_id: null }));
                return;
            }

            try {
                const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/product-groups/select", {
                    params: {
                        country: newData.country_id,
                    },
                });

                const groups = response.data?.data?.productGroups || response.data?.data?.groups || [];
                setProductGroups(groups);

                setNewData((prev) => {
                    if (prev.product_group_id === null || prev.product_group_id === "") {
                        return prev;
                    }

                    const hasSelectedGroup = groups.some((group) => Number(group.product_group_id) === Number(prev.product_group_id));
                    if (hasSelectedGroup) {
                        return prev;
                    }

                    return { ...prev, product_group_id: null };
                });
            } catch (error) {
                setProductGroups([]);
                console.log(error);
            }
        };

        fetchProductGroups();
    }, [open, newData.country_id]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const fetchMillionProducts = async () => {
            if (!newData.country_id) {
                setMillionProducts([]);
                setSelectedMillionProduct(null);
                return;
            }

            try {
                const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/million-products", {
                    params: {
                        page: 1,
                        perPage: 200,
                        country_id: newData.country_id,
                        status: "all",
                        search: millionSearch,
                    },
                });

                const products = response.data.data.millionProducts || [];

                // Keep one option per canonical Million code to avoid duplicate keys in autocomplete.
                const uniqueProducts = Object.values(
                    products.reduce((acc, item) => {
                        if (!item?.code) {
                            return acc;
                        }
                        if (!acc[item.code]) {
                            acc[item.code] = item;
                        }
                        return acc;
                    }, {})
                );

                setMillionProducts(uniqueProducts);

                setSelectedMillionProduct((prev) => {
                    if (!prev?.code) {
                        return prev;
                    }

                    const matchedSelected = uniqueProducts.find((item) => item.code === prev.code);
                    return matchedSelected || null;
                });

                if (initialData?.million_product_code && !selectedMillionProduct) {
                    const matchedProduct = uniqueProducts.find((item) => item.code === initialData.million_product_code);
                    if (matchedProduct) {
                        setSelectedMillionProduct(matchedProduct);
                    }
                }
            } catch (error) {
                console.log(error);
            }
        };

        fetchMillionProducts();
    }, [open, millionSearch, initialData, selectedMillionProduct, newData.country_id]);

    const handleCreateProduct = async () => {
        let isValid = true;

        if (newData["product_name"] === "") isValid = false;
        if (newData["product_description"] === "") isValid = false;
        if (newData["country_id"] === "") isValid = false;

        Object.keys(newData).forEach(async (field) => {
            await validate(field, newData[field]);
        });

        if (isValid) {
            await setErrorMessage("");
            await createProduct();
        } else {
            await setErrorMessage(`Please fill all the required fields.`);
        }
    };

    const createProduct = async () => {
        try {
            const { product_name, product_description, country_id, is_contract_product } = newData;
            const response = await axios.post(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/products/create", {
                product_name,
                product_description,
                country_id,
                product_group_id: newData.product_group_id === null || newData.product_group_id === "" ? null : Number(newData.product_group_id),
                is_contract_product,
                million_product_code: selectedMillionProduct?.code || undefined,
                price: selectedMillionProduct?.price ?? undefined,
            });

            if (response.status === 201) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Product created successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while creating the product. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Create New Product</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Product Details" />
                </Divider>
                <TextField
                    autoFocus
                    autoComplete="off"
                    margin="dense"
                    id="product_name"
                    label="Name"
                    type="text"
                    fullWidth
                    required
                    value={newData.product_name || ""}
                    error={errors.product_name}
                    helperText={helperText.product_name}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    onChange={(e) => {
                        setNewData({ ...newData, product_name: e.target.value ? e.target.value.toUpperCase() : "", });
                        validate("product_name", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="product_description"
                    label="Description"
                    type="product_description"
                    multiline
                    rows={3}
                    fullWidth
                    required
                    value={newData.product_description || ""}
                    error={errors.product_description}
                    helperText={helperText.product_description}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    onChange={(e) => {
                        setNewData({ ...newData, product_description: e.target.value ? e.target.value.toUpperCase() : "", });
                        validate("product_description", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="country_id"
                    select
                    label="Manage By"
                    type="number"
                    value={newData.country_id || ""}
                    fullWidth
                    required
                    error={errors.country_id}
                    helperText={helperText.country_id}
                    onChange={(e) => {
                        setNewData({ ...newData, country_id: e.target.value, product_group_id: null });
                        setSelectedMillionProduct(null);
                        setMillionSearch("");
                        validate("country_id", e.target.value);
                    }}
                >
                    {userCountry.map((country) => (
                        <MenuItem key={country.country_id} value={country.country_id}>
                            {country.country_name}
                        </MenuItem>
                    ))}
                </TextField>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={Number(newData.is_contract_product) === 1}
                            onChange={(e) => {
                                setNewData({ ...newData, is_contract_product: e.target.checked ? 1 : 0, product_group_id: e.target.checked ? newData.product_group_id : null });
                            }}
                        />
                    }
                    label="Contract Product"
                    sx={{ mt: 0.5 }}
                />
                {Number(newData.is_contract_product) === 1 && (
                    <TextField
                        autoComplete="off"
                        margin="dense"
                        id="product_group_id"
                        select
                        label="Product Group"
                        type="number"
                        value={newData.product_group_id === null ? "" : newData.product_group_id}
                        fullWidth
                        onChange={(e) => {
                            setNewData({ ...newData, product_group_id: e.target.value === "" ? null : e.target.value });
                        }}
                    >
                        <MenuItem value="">None</MenuItem>
                        {productGroups.map((group) => (
                            <MenuItem key={group.product_group_id} value={group.product_group_id}>
                                {group.group_name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                <Divider style={{ paddingTop: "12px", paddingBottom: "4px" }}>
                    <Chip label="Million Mapping (Optional)" />
                </Divider>

                <Autocomplete
                    options={millionProducts}
                    value={selectedMillionProduct}
                    disabled={isMillionMappingEntry}
                    disableClearable={isMillionMappingEntry}
                    onChange={(event, value) => {
                        setSelectedMillionProduct(value);
                    }}
                    onInputChange={(event, inputValue) => setMillionSearch(inputValue)}
                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                    getOptionLabel={(option) => `${option?.code || ""}${option?.description ? ` - ${option.description}` : ""}`}
                    renderOption={(props, option) => (
                        <li {...props} key={option.code}>
                            {option.code} - {option.description}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} margin="dense" label="Link Million Product" placeholder="Search by code or description" />}
                    fullWidth
                />

                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                    {selectedMillionProduct
                        ? `Selected mapping: ${selectedMillionProduct.code} | Price: ${selectedMillionProduct.price ?? "-"}`
                        : "No Million product selected."}
                </Typography>
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleCreateProduct} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Create
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
